import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDataByUrl } from '../../../actions/catalogs';
import {
	blue_pilu,
	getDayMoment,
	handleWarning,
	whichEnvURL,
} from '../../../helpers/utils';
import {
	onSnapshot,
	doc,
	collection,
	orderBy,
	query,
	where,
	limit,
} from 'firebase/firestore';
import { useState } from 'react';
import {
	Badge,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Tooltip,
	Typography,
} from '@mui/material';
import Brightness5Icon from '@mui/icons-material/Brightness5';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useDispatch, useSelector } from 'react-redux';
import { setShowSideBar, setSideBarStatus } from '../../../actions/sidebar';
import { db } from '../../../firebase/firebase-config';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import { NotificationsBar } from './NotificationsBar';
import { setNavBarStatus, setNavbarTheme } from '../../../actions/ui';
import { EMAILS_ALLOWED_FOR_READING_NOTIFICATIONS } from '../../utils/lists';

export const NavBar = () => {
	const currentVersion = localStorage.getItem('currentVersionPilu') || '';
	const [code, setCode] = useState('');
	const [lastVersion, setVersion] = useState('');
	const [buttonStyle, setButtonStyle] = useState({ color: '#ffffff' });
	const {
		sidebar = true,
		ui = null,
		user = null,
	} = useSelector((state) => state);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Sidebar
	const [openSidebar, setOpenSidebar] = useState(false);

	const handleClose = () => {
		setOpenSidebar(false);
	};

	// Notifications
	const [openNotificationsBar, setOpenNotificationsBar] = useState(false);

	const handleOpenNotificationsBar = () => setOpenNotificationsBar(true);
	const handleCloseNotificationsBar = () => setOpenNotificationsBar(false);

	// Notifications
	const [notifications, setNotifications] = useState([]);
	const [pendingNotifications, setPendingNotifications] = useState(0);

	// Use effect for UI theme
	useEffect(() => {
		setButtonStyle((old) => ({
			...old,
			color: ui?.theme === 'light' ? blue_pilu : '#ffffff',
		}));
	}, [ui?.theme]);

	useEffect(() => {
		// VERSION LISTENER
		const colRef = doc(db, 'updates', 'version');

		onSnapshot(colRef, (snapshot) => {
			setOpenSidebar(snapshot.data().version !== currentVersion);
			setVersion(snapshot.data());
		});

		if (EMAILS_ALLOWED_FOR_READING_NOTIFICATIONS.includes(user?.email)) {
			// NOTIFICATIONS LISTENER
			const colRefNotifications = query(
				collection(
					db,
					whichEnvURL(true) === 'live'
						? 'notifications'
						: 'notifications_stage'
				),
				where('isDone', '==', false),
				orderBy('created', 'asc'),
				limit(20)
			);

			onSnapshot(colRefNotifications, (snapshot) => {
				const items = snapshot.docs
					.map((doc) => ({
						id: doc.id,
						...doc.data(),
					}))
					.reverse();
				setPendingNotifications(
					items.filter(({ isDone }) => !isDone).length
				);
				setNotifications(items);
			});
		}

		return;
	}, []);

	const handleSearch = () => {
		if (code.length > 0) {
			getDataByUrl(
				`/api/order/?number_order__contains=${code.toUpperCase()}`
			).then(({ data }) => {
				if (data.count > 0) {
					// Get order object
					const order = data.results[0];

					// Path
					const path = `/dashboard/order/${order.id}`;

					// Set local storage
					localStorage.setItem('currentPathPilu', path);

					// Go to
					navigate(path);
				} else {
					handleWarning('No existe ninguna orden con ese código');
				}
				setCode('');
			});
		} else {
			handleWarning('Por favor escribe el código a buscar');
		}
	};

	return (
		<nav
			className={`navbar navbar-expand-lg ${
				ui?.theme === 'light' ? 'bg-light' : ''
			}`}
			style={{
				backgroundColor: ui?.theme === 'light' ? 'none' : blue_pilu,
			}}
		>
			{/* Notificatios bar */}
			<NotificationsBar
				pendingNotifications={pendingNotifications}
				open={openNotificationsBar}
				handleClose={handleCloseNotificationsBar}
				notifications={notifications}
			/>
			{/* Container navbar */}
			<div className='container-fluid'>
				{/* Open/Close sidebar */}
				<Tooltip title='Ver'>
					<IconButton
						style={buttonStyle}
						aria-label='profile'
						color='primary'
						onClick={() => {
							// Set new status
							const newStatus = !sidebar;
							// Change sidebar status (redux)
							dispatch(setShowSideBar(newStatus));
							// Change sidebar status (local storage)
							setSideBarStatus(newStatus);
						}}
					>
						{sidebar ? <MenuOpenIcon /> : <MenuIcon />}
					</IconButton>
				</Tooltip>

				{/* Navigation back */}
				<Tooltip title='Página anterior'>
					<IconButton
						style={buttonStyle}
						aria-label='profile'
						color='primary'
						onClick={() => {
							navigate(-1);
						}}
					>
						<KeyboardBackspaceIcon />
					</IconButton>
				</Tooltip>

				{/* Navbar color toggle */}
				<Tooltip
					title={`Cambiar tema - ${
						ui?.theme === 'light' ? 'Oscuro' : 'Ligero'
					}`}
				>
					<IconButton
						style={buttonStyle}
						aria-label='profile'
						color='primary'
						onClick={() => {
							// Choose new theme
							const newTheme =
								ui?.theme === 'light' ? 'dark' : 'light';

							// Set new status in Redux
							dispatch(setNavbarTheme(newTheme));

							// Set status in local storage
							setNavBarStatus(newTheme);
						}}
					>
						{ui?.theme === 'dark' ? (
							<Brightness5Icon />
						) : (
							<DarkModeIcon />
						)}
					</IconButton>
				</Tooltip>

				{lastVersion.version !== currentVersion && (
					<Dialog
						open={openSidebar}
						onClose={handleClose}
						aria-labelledby='alert-dialog-title'
						aria-describedby='alert-dialog-description'
					>
						<DialogTitle id='alert-dialog-title'>
							¡Nueva versión disponible ({lastVersion.version})
							🚀!
						</DialogTitle>
						<DialogContent>
							<List>
								{lastVersion?.features?.map((item, index) => (
									<ListItem key={index}>
										<ListItemText primary={`• ${item}`} />
									</ListItem>
								))}
							</List>
							<DialogContentText id='alert-dialog-description'>
								Para actualizar realiza el siguiente comando —{' '}
								<strong>Ctrl + Shift + R</strong>
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} autoFocus>
								Ok
							</Button>
						</DialogActions>
					</Dialog>
				)}
				<button
					className='navbar-toggler'
					type='button'
					data-bs-toggle='collapse'
					data-bs-target='#navbarSupportedContent'
					aria-controls='navbarSupportedContent'
					aria-expanded='false'
					aria-label='Toggle navigation'
				>
					<span className='navbar-toggler-icon'></span>
				</button>
				<div
					className='collapse navbar-collapse'
					id='navbarSupportedContent'
				>
					<ul className='navbar-nav me-auto mb-2 mb-lg-0'>
						<li className='nav-item'>
							<Typography
								variant='p'
								color='textSecondary'
								align='center'
								style={{
									color:
										ui?.theme === 'light'
											? blue_pilu
											: '#ffffff',
								}}
							>
								{getDayMoment(user?.first_name)}
							</Typography>
						</li>
					</ul>
					<div style={{ display: 'flex', flexDirection: 'row' }}>
						<input
							className='form-control me-2'
							type='search'
							placeholder='Número de orden'
							aria-label='Search'
							value={code}
							onChange={({ target }) => setCode(target.value)}
							onKeyDown={({ key }) => {
								if (key === 'Enter') {
									handleSearch();
								}
							}}
						/>
						<Tooltip title='Buscar'>
							<IconButton
								style={buttonStyle}
								aria-label='profile'
								color='primary'
								onClick={handleSearch}
							>
								<SearchIcon />
							</IconButton>
						</Tooltip>
					</div>

					{/* NOTIFICATIONS */}
					<IconButton
						style={buttonStyle}
						aria-label='profile'
						color='primary'
						onClick={handleOpenNotificationsBar}
					>
						<Tooltip title='Notificaciones'>
							<Badge
								badgeContent={pendingNotifications}
								color='primary'
							>
								<NotificationsIcon />
							</Badge>
						</Tooltip>
					</IconButton>
				</div>
			</div>
		</nav>
	);
};
